<template>
  <main class="guide-page">
    <b-container class="py-5">
      <div class="guide-layout">
        <article>
          <header class="mb-5">
            <p class="eyebrow">SHOPPING GUIDE</p>
            <h1>The Practical Guide to Online Cash Back</h1>
            <p class="lead">How rewards are funded, how tracking works, and how to avoid the most common reasons a purchase does not qualify.</p>
            <p class="text-muted small">Reviewed July 26, 2026 · CashBoomerang Editorial Team</p>
          </header>

          <section id="definition">
            <h2>What is online cash back?</h2>
            <p>Online cash back is a share of the commission that an eligible retailer pays a rewards publisher after a verified purchase. It is not an instant discount from the product price. A reward can be paid only after the retailer reports and approves the transaction.</p>
          </section>

          <section id="tracking">
            <h2>How transaction tracking works</h2>
            <p>When you activate an eligible offer, a unique shopping-trip reference is sent to the participating affiliate network. If the retailer later reports a qualifying order with that reference, CashBoomerang can match it to your account. The retailer—not CashBoomerang—makes the final eligibility decision.</p>
          </section>

          <section id="checklist">
            <h2>Eligibility checklist</h2>
            <ul class="check-list">
              <li>The offer was marked Cash Back Eligible before you clicked.</li>
              <li>You were signed in and used CashBoomerang’s activation button.</li>
              <li>The cart was empty before the shopping trip began.</li>
              <li>No unrelated browser extension or competing rewards site replaced the referral.</li>
              <li>The coupon code, product category, and purchase method were allowed by the offer terms.</li>
              <li>The order was not returned, cancelled, refunded, or flagged as duplicate or fraudulent.</li>
            </ul>
          </section>

          <section id="timing">
            <h2>Why rewards stay pending</h2>
            <p>Retailers usually wait until fulfillment and the return period have passed. Travel, subscription, and service purchases can take longer because approval may depend on the stay, renewal, or service completion date. CashBoomerang will show a reward as available only after approval.</p>
          </section>

          <section id="affiliate-links">
            <h2>Cash-back offers versus affiliate shopping links</h2>
            <p>Not every monetized link is a cash-back offer. Some links help fund CashBoomerang through an affiliate commission but do not permit user incentives. We label those as shopping deals and disclose the relationship near the link. Amazon Associates links fall into this category and do not earn CashBoomerang user rewards.</p>
          </section>

          <section id="missing">
            <h2>What to save if a reward is missing</h2>
            <p>Keep the order number, retailer name, order date, subtotal before tax and shipping, and the confirmation email. Do not send a full card number or account password. A missing-reward review can begin only after the retailer’s normal reporting window has passed.</p>
          </section>

          <div class="next-card">
            <h2>Ready to learn the flow?</h2>
            <p>See each reward status and the steps that happen between an activated offer and a payout.</p>
            <router-link to="/how-cashback-works" class="btn btn-success">How cash back works</router-link>
            <router-link to="/faq" class="btn btn-outline-success ml-2">Read the FAQ</router-link>
          </div>
        </article>

        <aside class="toc" aria-label="On this page">
          <strong>On this page</strong>
          <a href="#definition">Cash-back definition</a>
          <a href="#tracking">Transaction tracking</a>
          <a href="#checklist">Eligibility checklist</a>
          <a href="#timing">Pending rewards</a>
          <a href="#affiliate-links">Affiliate-link disclosure</a>
          <a href="#missing">Missing rewards</a>
        </aside>
      </div>
    </b-container>
  </main>
</template>

<script>
export default { name: 'CashbackGuide' }
</script>

<style scoped>
.guide-page { background: #fff; color: #24342a; min-height: 75vh; }
.guide-layout { display: grid; grid-template-columns: minmax(0, 760px) 230px; gap: 64px; justify-content: center; }
article h1 { font-size: clamp(2.1rem, 5vw, 3.4rem); font-weight: 700; letter-spacing: -.03em; }
article section { margin-bottom: 3rem; }
article h2 { font-size: 1.65rem; font-weight: 700; margin-bottom: 1rem; }
article p, article li { line-height: 1.8; }
.eyebrow { color: #198754; font-weight: 700; letter-spacing: .14em; }
.check-list { padding-left: 1.3rem; }
.check-list li { margin-bottom: .65rem; }
.toc { position: sticky; top: 170px; align-self: start; border-left: 2px solid #dce9df; padding-left: 20px; display: flex; flex-direction: column; gap: 10px; }
.toc a { color: #4c5e52; font-size: .92rem; }
.next-card { background: #eff8f1; border-radius: 18px; padding: 28px; }
@media (max-width: 900px) { .guide-layout { grid-template-columns: 1fr; } .toc { display: none; } }
</style>

